import React, { useEffect, useMemo, useState } from "react";
import { Snackbar, Alert, Button } from "@mui/material";
import { LuX } from "react-icons/lu";
import { Avatar, InviteStatusPill, SourcePill, formatInvitedDate } from "./sharedUI";

const StatCard = ({ label, value }) => (
    <div className="icv2-stat-card">
        <div className="icv2-stat-value">{value}</div>
        <div className="icv2-stat-label">{label}</div>
    </div>
);

// Normalises an invite record from GET /jobs/{job_id}/candidate-invites/
// (invites[] with nested status + candidate) into a flat row.
const mapInvite = (inv) => {
    const c = inv.candidate || {};
    const status = inv.status || {};
    return {
        id: inv.id || inv.invite_id,
        name: c.full_name || c.name || inv.candidate_name || "Unknown",
        email: c.email || inv.candidate_email || "",
        phone: c.phone_number || c.phone || inv.candidate_phone || "",
        title: c.current_designation || c.title || c.headline || "",
        invitedAt: inv.created_at || inv.invited_at || inv.updated_at || null,
        // created_at is the first invite time; the 72h re-invite cooldown runs from it
        firstInvitedAt: inv.created_at || inv.invited_at || null,
        canReinvite: typeof inv.can_reinvite === "boolean" ? inv.can_reinvite : null,
        statusLabel: status.name || status.label || inv.status_name || "Invited",
        statusSlug: status.slug || status.code || inv.status_slug || "",
        statusColor: status.color || status.color_code || null,
        source: inv.source || "smart_search",
    };
};

const statusKey = (row) =>
    (row.statusSlug || row.statusLabel || "").toString().toLowerCase().replace(/[\s-]+/g, "_");

const REINVITE_COOLDOWN_MS = 72 * 60 * 60 * 1000;
const REINVITABLE_STATUSES = ["invited", "invite_sent", "no_response"];
const isApplied = (row) => statusKey(row) === "applied";
const isReinvited = (row) => ["re_invited", "reinvited"].includes(statusKey(row));

// One re-invite per candidate, allowed 72h after the first invite, for Invited /
// No Response only. The backend's `can_reinvite` is the source of truth; the
// status check is only a fallback for payloads that don't carry it yet.
const canResend = (row) =>
    row.canReinvite !== null ? row.canReinvite : REINVITABLE_STATUSES.includes(statusKey(row));

const formatAllowedAt = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

// When the row is still inside the 72h cooldown, the time re-invite opens up.
const cooldownEndsAt = (row) => {
    if (!row.firstInvitedAt || !REINVITABLE_STATUSES.includes(statusKey(row))) return null;
    const start = new Date(row.firstInvitedAt).getTime();
    if (Number.isNaN(start)) return null;
    const end = start + REINVITE_COOLDOWN_MS;
    return end > Date.now() ? new Date(end).toISOString() : null;
};

// Why a row can't be re-invited, for the disabled checkbox / button tooltip.
const blockedReason = (row) => {
    if (isApplied(row)) return "Already applied";
    if (isReinvited(row)) return "Already re-invited";
    if (!row.email) return "No email address";
    const until = cooldownEndsAt(row);
    if (until) return `Re-invite available from ${formatAllowedAt(until)}`;
    return "Can't be re-invited in the current status";
};

const SKIP_REASON_TEXT = {
    max_reinvites_reached: "Already re-invited",
    already_applied: "Already applied",
    already_in_job: "Already in this job's pipeline",
    invalid_status: "Can't be re-invited in the current status",
    cannot_email: "No email address",
    not_found: "Invite not found",
    email_failed: "Email couldn't be sent, please try again",
};

const skipReasonText = (item) => {
    if (item.reason === "cooldown_active") {
        const when = formatAllowedAt(item.next_allowed_at);
        return when ? `Invited less than 3 days ago. Available from ${when}` : "Invited less than 3 days ago";
    }
    return SKIP_REASON_TEXT[item.reason] || item.reason || "Skipped";
};

const InvitedCandidatesTab = ({
    invites = [],
    summary = { invited: 0, applied: 0, no_response: 0, re_invited: 0, total: 0 },
    loading = false,
    hiringStatuses = [],
    page = 1,
    pageSize = 20,
    statusId = "",
    pagination = null,
    onPageChange,
    onPageSizeChange,
    onStatusChange,
    onResend,
    onBulkResend,
    onRefresh,
}) => {
    const [search, setSearch] = useState("");
    const [sortAsc, setSortAsc] = useState(false);
    const [resendingId, setResendingId] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
    // id -> name, so skipped rows can still be named after the list refetches
    const [selected, setSelected] = useState({});
    const [bulkSending, setBulkSending] = useState(false);
    const [skippedList, setSkippedList] = useState([]);
    const [showSkipped, setShowSkipped] = useState(false);

    const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

    // Selection is scoped to the visible page / filter
    useEffect(() => {
        setSelected({});
    }, [page, pageSize, statusId]);

    const rows = useMemo(() => {
        const term = search.trim().toLowerCase();
        let list = invites.map(mapInvite).filter((r) => {
            if (!term) return true;
            return r.name.toLowerCase().includes(term) || r.email.toLowerCase().includes(term);
        });
        list.sort((a, b) => {
            const diff = new Date(a.invitedAt || 0) - new Date(b.invitedAt || 0);
            return sortAsc ? diff : -diff;
        });
        return list;
    }, [invites, search, sortAsc]);

    const total = pagination?.total ?? summary?.total ?? invites.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const currentPage = Math.min(page, totalPages);

    const handleResend = async (row) => {
        if (!onResend || resendingId || bulkSending) return;
        setResendingId(row.id);
        const res = await onResend({ id: row.id });
        setResendingId(null);
        if (res?.success && !res.emailSent) {
            setSnackbar({ open: true, message: "Couldn't send the invite, please try again.", severity: "error" });
            return;
        }
        setSnackbar({
            open: true,
            message: res?.success
                ? `${row.name} re-invited.`
                : res?.message || "Failed to resend invite.",
            severity: res?.success ? "success" : "error",
        });
    };

    const selectableRows = rows.filter(canResend);
    const selectedIds = Object.keys(selected);
    const allSelected = selectableRows.length > 0 && selectableRows.every((r) => selected[r.id]);

    const toggleRow = (row) => {
        setSelected((prev) => {
            const next = { ...prev };
            if (next[row.id]) delete next[row.id];
            else next[row.id] = row.name;
            return next;
        });
    };

    const toggleAll = () => {
        if (allSelected) {
            setSelected({});
        } else {
            setSelected(Object.fromEntries(selectableRows.map((r) => [r.id, r.name])));
        }
    };

    const handleBulkResend = async () => {
        if (!onBulkResend || bulkSending || selectedIds.length === 0) return;
        const names = { ...selected };
        setBulkSending(true);
        const res = await onBulkResend(selectedIds);
        setBulkSending(false);
        if (!res?.success) {
            setSnackbar({ open: true, message: res?.message || "Failed to re-invite candidates.", severity: "error" });
            return;
        }
        setSelected({});
        const skipped = (res.skipped || []).map((s) => ({ ...s, name: names[s.invite_id] || "Unknown candidate" }));
        setSkippedList(skipped);
        const plural = (n) => (n === 1 ? "candidate" : "candidates");
        const message = skipped.length
            ? `${res.reinvited} ${plural(res.reinvited)} re-invited, ${skipped.length} skipped`
            : `${res.reinvited} ${plural(res.reinvited)} re-invited`;
        setSnackbar({
            open: true,
            message,
            severity: skipped.length ? (res.reinvited ? "warning" : "error") : "success",
            showSkipped: skipped.length > 0,
        });
    };

    const renderPagination = () => {
        const pages = [];
        pages.push(
            <button key="prev" className="icv2-page-btn" onClick={() => onPageChange?.(currentPage - 1)} disabled={currentPage === 1}>
                ‹
            </button>
        );
        pages.push(
            <button key={1} className={`icv2-page-btn${currentPage === 1 ? " active" : ""}`} onClick={() => onPageChange?.(1)}>1</button>
        );
        if (currentPage > 3) pages.push(<span key="ldots" className="icv2-page-ellipsis">…</span>);
        const start = Math.max(2, currentPage - 1);
        const end = Math.min(totalPages - 1, currentPage + 1);
        for (let i = start; i <= end; i++) {
            pages.push(
                <button key={i} className={`icv2-page-btn${currentPage === i ? " active" : ""}`} onClick={() => onPageChange?.(i)}>{i}</button>
            );
        }
        if (currentPage < totalPages - 2) pages.push(<span key="rdots" className="icv2-page-ellipsis">…</span>);
        if (totalPages > 1) {
            pages.push(
                <button key={totalPages} className={`icv2-page-btn${currentPage === totalPages ? " active" : ""}`} onClick={() => onPageChange?.(totalPages)}>{totalPages}</button>
            );
        }
        pages.push(
            <button key="next" className="icv2-page-btn" onClick={() => onPageChange?.(currentPage + 1)} disabled={currentPage === totalPages}>
                ›
            </button>
        );
        return pages;
    };

    return (
        <div className="icv2-invited-tab">
            <div className="icv2-invited-header">
                <div className="icv2-invited-header-text">
                    <span className="icv2-eyebrow">Outreach overview</span>
                    <h2>Invited candidates</h2>
                    <p>Stay close to every conversation, from first send to application.</p>
                </div>
                <div className="icv2-stat-cards">
                    <StatCard label="Invited" value={summary?.invited ?? 0} />
                    <StatCard label="Applied" value={summary?.applied ?? 0} />
                    <StatCard label="No response" value={summary?.no_response ?? 0} />
                    <StatCard label="Re-invited" value={summary?.re_invited ?? 0} />
                    <StatCard label="Total" value={summary?.total ?? 0} />
                </div>
            </div>

            <div className="icv2-invited-toolbar">
                <div className="icv2-search-box">
                    <img src="/images/search.svg" alt="" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search invited candidates…"
                    />
                </div>
                <div className="icv2-toolbar-right">
                    {selectedIds.length > 0 && (
                        <button
                            className="icv2-bulk-reinvite-btn"
                            onClick={handleBulkResend}
                            disabled={bulkSending}
                        >
                            {bulkSending ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" role="status" />
                                    Re-inviting…
                                </>
                            ) : (
                                `Re-invite selected (${selectedIds.length})`
                            )}
                        </button>
                    )}
                    <span className="icv2-showing-label">Showing</span>
                    <select
                        className="icv2-status-select"
                        value={statusId}
                        onChange={(e) => onStatusChange?.(e.target.value)}
                    >
                        <option value="">All</option>
                        {hiringStatuses.map((s) => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                    </select>
                    <button
                        className="icv2-sort-toggle"
                        onClick={() => setSortAsc((v) => !v)}
                        title={sortAsc ? "Oldest first" : "Newest first"}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: sortAsc ? "rotate(180deg)" : "none" }}>
                            <polyline points="6 9 12 15 18 9" />
                        </svg>
                    </button>
                    <button className="icv2-sort-toggle" onClick={() => onRefresh?.()} title="Refresh">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="23 4 23 10 17 10" />
                            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                        </svg>
                    </button>
                </div>
            </div>

            <div className="icv2-invited-table-wrap">
                <table className="icv2-invited-table">
                    <thead>
                        <tr>
                            <th className="icv2-th-select">
                                <input
                                    type="checkbox"
                                    checked={allSelected}
                                    disabled={selectableRows.length === 0 || bulkSending}
                                    onChange={toggleAll}
                                    aria-label="Select all candidates that can be re-invited"
                                />
                            </th>
                            <th>Candidate</th>
                            <th>Contact</th>
                            <th>Source</th>
                            <th>Invited</th>
                            <th>Status</th>
                            <th className="icv2-th-actions">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={7} className="icv2-table-empty">Loading invited candidates…</td>
                            </tr>
                        ) : rows.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="icv2-table-empty">
                                    No invited candidates yet. Add candidates from the &ldquo;Add candidates&rdquo; tab.
                                </td>
                            </tr>
                        ) : (
                            rows.map((c) => {
                                const allowed = canResend(c);
                                const reason = allowed ? "" : blockedReason(c);
                                const cooldownUntil = cooldownEndsAt(c);
                                return (
                                <tr key={c.id}>
                                    <td className="icv2-th-select">
                                        {/* span carries the tooltip: disabled inputs don't get hover events */}
                                        <span title={reason}>
                                            <input
                                                type="checkbox"
                                                checked={allowed && Boolean(selected[c.id])}
                                                disabled={!allowed || bulkSending}
                                                onChange={() => toggleRow(c)}
                                                aria-label={`Select ${c.name}`}
                                            />
                                        </span>
                                    </td>
                                    <td>
                                        <div className="icv2-candidate-cell">
                                            <Avatar name={c.name} size={34} />
                                            <div>
                                                <strong>{c.name}</strong>
                                                {c.title && <p>{c.title}</p>}
                                            </div>
                                        </div>
                                    </td>
                                    <td>{c.email || "—"}</td>
                                    <td><SourcePill source={c.source} /></td>
                                    <td>{formatInvitedDate(c.invitedAt)}</td>
                                    <td>
                                        <InviteStatusPill label={c.statusLabel} slug={c.statusSlug} color={c.statusColor} />
                                        {!allowed && cooldownUntil && (
                                            <p className="icv2-reinvite-hint">
                                                Re-invite available from {formatAllowedAt(cooldownUntil)}
                                            </p>
                                        )}
                                    </td>
                                    <td className="icv2-th-actions">
                                        <div className="icv2-row-actions">
                                            {!isApplied(c) && (
                                                <span title={allowed ? "Re-invite" : reason}>
                                                    <button
                                                        className="icv2-icon-btn"
                                                        disabled={!allowed || resendingId === c.id || bulkSending}
                                                        onClick={() => handleResend(c)}
                                                        aria-label={`Re-invite ${c.name}`}
                                                    >
                                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <polyline points="23 4 23 10 17 10" />
                                                            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                                                        </svg>
                                                    </button>
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            <div className="icv2-pagination">
                <span className="icv2-pagination-info">
                    {total ? (
                        <>
                            Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, total)} of{" "}
                            <strong>{total}</strong> candidates
                        </>
                    ) : (
                        "Showing 0 candidates"
                    )}
                </span>
                <div className="icv2-pagination-right">
                    <div className="icv2-page-btns">{renderPagination()}</div>
                    <div className="icv2-per-page">
                        <span>Per page:</span>
                        <select
                            value={pageSize}
                            onChange={(e) => onPageSizeChange?.(parseInt(e.target.value))}
                        >
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                </div>
            </div>

            <Snackbar open={snackbar.open} autoHideDuration={snackbar.showSkipped ? 8000 : 3000} onClose={closeSnackbar} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
                <Alert
                    onClose={closeSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: "100%", alignItems: "center" }}
                    action={snackbar.showSkipped ? (
                        <Button
                            color="inherit"
                            size="small"
                            onClick={() => { closeSnackbar(); setShowSkipped(true); }}
                        >
                            View skipped
                        </Button>
                    ) : undefined}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {showSkipped && (
                <div className="icv2-chat-modal-overlay" onClick={() => setShowSkipped(false)}>
                    <div
                        className="icv2-skipped-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="icv2-skipped-title"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="icv2-chat-modal-head">
                            <h3 id="icv2-skipped-title">Skipped candidates ({skippedList.length})</h3>
                            <button className="icv2-chat-modal-close" onClick={() => setShowSkipped(false)} aria-label="Close">
                                <LuX size={18} />
                            </button>
                        </div>
                        <ul className="icv2-skipped-list">
                            {skippedList.map((s) => (
                                <li key={s.invite_id}>
                                    <Avatar name={s.name} size={30} />
                                    <div>
                                        <strong>{s.name}</strong>
                                        <p>{skipReasonText(s)}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InvitedCandidatesTab;
